import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Bell, UserPlus, GraduationCap, PlayCircle, ShieldCheck, Clock,
  Wallet, AlertTriangle, Check, X,
} from "lucide-react";

const FILTERS = [["all", "All"], ["action", "Needs action"], ["leads", "Leads"], ["training", "Training"]];

const META = {
  lead_new: { icon: UserPlus, tint: "#4060FF" },
  lead_update: { icon: UserPlus, tint: "#4060FF" },
  lead_quiet: { icon: Clock, tint: "#D97706" },
  training_video: { icon: PlayCircle, tint: "#0F7A4A" },
  training_progress: { icon: GraduationCap, tint: "#0F7A4A" },
  training_done: { icon: GraduationCap, tint: "#0F7A4A" },
  approval_needed: { icon: ShieldCheck, tint: "#7C3AED" },
  member_ready: { icon: Wallet, tint: "#7C3AED" },
  member_blocked: { icon: AlertTriangle, tint: "#DC2626" },
  member_created: { icon: Check, tint: "#0F7A4A" },
};

const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 604800) return `${Math.round(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0, needs_action: 0 });
  const [filter, setFilter] = useState("all");
  const panelRef = useRef(null);
  const autoRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/notifications", { params: { limit: 80 } });
      setData(d || { items: [], unread: 0, needs_action: 0 });
      return d;
    } catch { return null; }
  }, []);

  useEffect(() => {
    if (!user || user.is_demo) return;
    let live = true;
    const tick = async () => {
      const d = await load();
      // Pop the panel open once per sign-in when something is waiting.
      if (live && d?.unread > 0 && !autoRef.current && !sessionStorage.getItem("cp_notif_opened")) {
        autoRef.current = true;
        sessionStorage.setItem("cp_notif_opened", "1");
        setOpen(true);
      }
    };
    tick();
    const t = setInterval(load, 90000);
    return () => { live = false; clearInterval(t); };
  }, [user, load]);

  const close = useCallback(async () => {
    setOpen(false);
    if (data.unread > 0) {
      setData((d) => ({ ...d, unread: 0, items: d.items.map((i) => ({ ...i, unread: false })) }));
      try { await api.post("/notifications/mark-read"); } catch { /* best effort */ }
    }
  }, [data.unread]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) close(); };
    const onEsc = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open, close]);

  const shown = useMemo(
    () => (filter === "all" ? data.items : data.items.filter((i) => i.group === filter)),
    [data.items, filter],
  );

  if (!user || user.is_demo) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        title="Notifications — leads & training activity under your link"
        data-testid="notif-bell-btn"
        className={`relative px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${open ? "bg-white text-ink shadow-sm" : "text-ink-2 hover:bg-white/60"}`}
      >
        <Bell size={13} />
        <span className="hidden sm:inline">Alerts</span>
        {data.unread > 0 && (
          <span
            className="min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
            style={{ background: data.needs_action > 0 ? "#DC2626" : "#4060FF" }}
            data-testid="notif-badge"
          >
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[min(92vw,420px)] backdrop-blur-xl shadow-2xl z-50 overflow-hidden"
          style={{ borderRadius: 16, border: "1px solid rgba(15,18,38,0.08)", background: "rgba(255,255,255,0.985)" }}
          data-testid="notif-panel"
        >
          <div className="px-3.5 pt-3 pb-2 flex items-start justify-between gap-2">
            <div>
              <div className="text-sm font-bold text-ink">Notifications</div>
              <div className="text-[11px] text-ink-3" data-testid="notif-summary">
                {data.needs_action > 0 ? `${data.needs_action} need${data.needs_action === 1 ? "s" : ""} action` : "Everything is handled"}
                {data.unread > 0 ? ` · ${data.unread} new` : ""}
              </div>
            </div>
            <button type="button" onClick={close} className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-3 hover:bg-black/5" data-testid="notif-close" aria-label="Close">
              <X size={15} />
            </button>
          </div>

          <div className="px-3 pb-2 flex items-center gap-1 flex-wrap">
            {FILTERS.map(([k, label]) => {
              const n = k === "all" ? data.items.length : data.items.filter((i) => i.group === k).length;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFilter(k)}
                  data-testid={`notif-filter-${k}`}
                  className="px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors"
                  style={filter === k
                    ? { background: "#0F1226", color: "white" }
                    : { background: "rgba(15,18,38,0.05)", color: "#6E6889" }}
                >
                  {label}{n > 0 ? ` ${n}` : ""}
                </button>
              );
            })}
          </div>

          <div className="max-h-[62vh] overflow-y-auto border-t border-black/5">
            {shown.length === 0 ? (
              <div className="px-4 py-8 text-center text-[12px] text-ink-3" data-testid="notif-empty">
                Nothing here yet — new leads and training activity under your link will show up.
              </div>
            ) : shown.map((it) => {
              const m = META[it.kind] || { icon: Bell, tint: "#6E6889" };
              const Icon = m.icon;
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => { if (it.link) navigate(it.link); close(); }}
                  data-testid={`notif-item-${it.kind}`}
                  className="w-full text-left px-3.5 py-2.5 flex gap-2.5 border-b border-black/5 hover:bg-black/[0.02] transition-colors"
                  style={it.unread ? { background: "rgba(64,96,255,0.045)" } : undefined}
                >
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${m.tint}1A` }}>
                    <Icon size={14} style={{ color: m.tint }} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-baseline gap-2">
                      <span className="text-[12.5px] font-semibold text-ink leading-snug flex-1">{it.title}</span>
                      <span className="text-[10px] text-ink-3 font-mono whitespace-nowrap">{ago(it.at)}</span>
                    </span>
                    {it.body && <span className="block text-[11.5px] text-ink-2 leading-snug mt-0.5">{it.body}</span>}
                    <span className="flex items-center gap-1.5 mt-1">
                      {it.needs_action && (
                        <span className="chip text-[10px]" style={{ background: "rgba(220,38,38,0.10)", color: "#DC2626", fontWeight: 800 }}>
                          Action needed
                        </span>
                      )}
                      {it.link_label && <span className="text-[10.5px] font-bold" style={{ color: m.tint }}>{it.link_label} →</span>}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {data.items.length > 0 && (
            <button
              type="button"
              onClick={close}
              className="w-full py-2.5 text-[11.5px] font-bold text-ink-2 hover:bg-black/[0.03] transition-colors"
              data-testid="notif-mark-read"
            >
              Mark all as read
            </button>
          )}
        </div>
      )}
    </div>
  );
}
